/**
 * Protected route component
 *
 * Wraps routes that require authentication. Redirects unauthenticated
 * users to login and shows a loading state during auth check.
 */

import type { ReactNode } from "react";

import { useAuth } from "@/contexts/AuthContext";

interface ProtectedRouteProps {
	children: ReactNode;
}

/**
 * Protected route wrapper component
 *
 * Renders children only when user is authenticated.
 * Shows loading state during auth check.
 * Redirects to login when unauthenticated.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
	const { isAuthenticated, isLoading } = useAuth();

	// Show loading state during auth check
	if (isLoading) {
		return <LoadingSpinner />;
	}

	// Show login prompt for unauthenticated users
	if (!isAuthenticated) {
		return <LoginPrompt />;
	}

	// Render protected content
	return <>{children}</>;
}

/**
 * Loading spinner component for auth check state
 */
function LoadingSpinner() {
	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50">
			<div className="text-center">
				<div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
				<p className="mt-4 text-sm text-gray-500">Checking authentication...</p>
			</div>
		</div>
	);
}

/**
 * Login prompt component for unauthenticated users
 */
function LoginPrompt() {
	const { login } = useAuth();

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50">
			<div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
				<div className="text-center">
					<h1 className="text-xl font-semibold text-gray-900">
						OpSignal Video Uploads
					</h1>
					<p className="mt-2 text-sm text-gray-500">
						Sign in to upload and manage your video files.
					</p>
					<button className="btn-primary mt-6 w-full" onClick={() => login()}>
						Sign in
					</button>
				</div>
			</div>
		</div>
	);
}
